import * as _ from 'lodash'
import enums from './enum';
import util from './util';

const EXPRESSIONS = {
	// eslint-disable-next-line no-control-regex,no-useless-escape
	email: /^((([a-z]|\d|[!#\$%&'\*\+\-\/=\?\^_`{\|}~]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])+(\.([a-z]|\d|[!#\$%&'\*\+\-\/=\?\^_`{\|}~]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])+)*)|((\x22)((((\x20|\x09)*(\x0d\x0a))?(\x20|\x09)+)?(([\x01-\x08\x0b\x0c\x0e-\x1f\x7f]|\x21|[\x23-\x5b]|[\x5d-\x7e]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(\\([\x01-\x09\x0b\x0c\x0d-\x7f]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]))))*(((\x20|\x09)*(\x0d\x0a))?(\x20|\x09)+)?(\x22)))@((([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])*([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])))\.)+(([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])*([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])))\.?$/i
};

function _isUndefined (val) {
	return typeof val === 'undefined' || val === null || val === '';
}

function _invalidValueMessage (property, val, message?) {
	const propName = _.startCase(property);

	return message || ((val !== 0 && !val) ? propName + ' cannot be empty.' : val + ' isn\'t a valid value for ' + propName + '.');
}

const validator = {

	validate: function (target, rules, rootMessage) {
		const messages = validator.test(target, rules);

		if (!messages) {
			return;
		}

		let message = messages.join('\n');

		if (rootMessage) {
			message = rootMessage + '\n' + message;
		}
		validator.throwValidationError(message, target);
	},

	test: function (target, rules, messages?) {
		if (!rules) {
			throw new Error('No rules provided');
		}

		let results = messages;
		let result;

		Object.keys(rules).forEach(function (key) {
			if (key === '_model_') {
				// return the text for the first broken rule
				for (let i = 0, n = rules._model_.length; i < n; i++) {
					result = rules._model_[i](target);

					if (typeof result === 'string' && result !== '') {
						if (results) {
							results.push(result);
						}
						else {
							results = [result];
						}
					}
				}
			}
			else {
				const propertyRules = rules[key];

				result = validator.testProperty(target, key, propertyRules);

				if (typeof result === 'string' && result !== '') {
					results = results || [];
					results.push(result);
				}
			}
		});

		return results;
	},

	validateProperty: function (target, property, rules) {
		const message = validator.testProperty(target, property, rules);

		if (!message) {
			return;
		}

		validator.throwValidationError(message, target);
	},

	testProperty: function (target, property, rules, messages?) {
		if (typeof target === 'undefined') { return undefined; }
		if (!Array.isArray(rules)) {
			rules = [rules];
		}

		const value = target[property];

		// return the text for the first broken rule
		for (let i = 0, n = rules.length; i < n; i++) {
			const result: string = rules[i](value, target, property);

			if (typeof result === 'string' && result !== '') {
				if (messages) { messages.push(result); }
				return result;
			}
		}
		return undefined;
	},

	throwValidationError: function (message, target?) {
		throw validator.createValidationError(message, target);
	},

	createValidationError: function (message, target) {
		const error = new Error(message);

		(error as any).target = target;
		(error as any).type = 'validation';
		throw error;
	},

	required: function (message?) {
		return function (value, target, property) {
			const hasValue = validator.hasValue(value);

			return hasValue || message || _.startCase(property) + ' is required.';
		};
	},

	validModel: function (rules, message?) {
		return function (value, target, property) {
			let results:string[] = [];
			const isArray = _.isArray(value);

			if (isArray || (value && typeof value.toDocumentArray === 'function')) {
				let i = 1;
				const iterate = (isArray) ? _.forEach : _.forIn;

				iterate(value, function (x) {
					if (typeof x === 'function') { return; }

					const itemResults = validator.test(x, rules);

					if (itemResults && itemResults.length) {
						itemResults.splice(0, 0, message || _.startCase(property) + ' ' + i + ' is not valid.');
						results = results.concat(itemResults);
					}
					i++;
				});
			}
			else {
				results = validator.test(value, rules, results);
				if (results && results.length) {
					results.splice(0, 0, message || _.startCase(property) + ' is not valid.');
				}
			}

			const result = (results.length) ? results.join('\n') : '';

			return (typeof result === 'string' && result !== '') ? result : undefined;
		};
	},

	match: function (expression, allowUndefined?, message?) {
		return function (value, target, property) {
			return validator.isMatch(value, expression, allowUndefined) || _invalidValueMessage(property, value, message);
		};
	},

	email: function (allowUndefined?, message?) {
		return function (value, target, property) {
			return validator.isEmail(value, allowUndefined) || _invalidValueMessage(property, value, message);
		};
	},

	phone: function (allowUndefined?, message?) {
		return function (value, target, property) {
			const propName = enums.phoneNumberTypes.getName(target.type);

			message = message || propName + ' is not a valid phone number.';
			return validator.isPhone(value, allowUndefined) || _invalidValueMessage(property, value, message);
		};
	},

	date: function (allowUndefined?, message?) {
		return function (value, target, property) {
			return validator.isDate(value, allowUndefined) || _invalidValueMessage(property, value, message);
		};
	},

	number: function (allowUndefined?, message?) {
		return function (value, target, property) {
			return validator.isNumber(value, allowUndefined) || _invalidValueMessage(property, value, message);
		};
	},

	integer: function (allowUndefined?, message?) {
		return function (value, target, property) {
			return validator.isInteger(value, allowUndefined) || _invalidValueMessage(property, value, message);
		};
	},

	bool: function (allowUndefined?, message?) {
		return function (value, target, property) {
			return validator.isBool(value, allowUndefined) || _invalidValueMessage(property, value, message);
		};
	},

	string: function (allowUndefined?, message?) {
		return function (value, target, property) {
			return validator.isString(value, allowUndefined) || _invalidValueMessage(property, value, message);
		};
	},

	enum: function (enumKey, disallowZero?, message?) {
		if (!enumKey) {
			throw new Error('validator.isEnum requires an enum key');
		}

		return function (value, target, property) {
			return validator.isEnum(value, enumKey, disallowZero) || _invalidValueMessage(property, value, message);
		};
	},

	inList: function (list, message?) {
		if (!list && list.length) {
			throw new Error('validator.inList requires an array');
		}
		return function (value, target, property) {
			return (list.indexOf(value) !== -1) || _invalidValueMessage(property, value, message);
		};
	},

	inRange: function (min, max, message?) {
		if (!_.isNumber(min) || !_.isNumber(max)) {
			throw new Error('validator.inRange requires an number');
		}
		return function (value, target, property) {
			const isValid = (value >= min && value <= max);

			return isValid || message || _invalidValueMessage(property, value);
		};
	},

	greaterThan: function (min, message?) {
		if (!_.isNumber(min)) {
			throw new Error('validator.greaterThan requires an number');
		}
		return function (value, target, property) {
			const isValid = (value > min);

			return isValid || message || _invalidValueMessage(property, value);
		};
	},

	lessThan: function (max, message?) {
		if (!_.isNumber(max)) {
			throw new Error('validator.lessThan requires an number');
		}
		return function (value, target, property) {
			const isValid = (value < max);

			return isValid || message || _invalidValueMessage(property, value);
		};
	},

	length: function (min, max, message?) {
		return function (value, target, property) {
			const isValid = (value.length >= min && value.length <= max);

			return isValid || _invalidValueMessage(property, value, message);
		};
	},

	not: function (invalidValue, message?) {
		return function (value, target, property) {
			const isValid = invalidValue !== value;

			return isValid || _invalidValueMessage(property, value, message);
		};
	},

	hasValue: function (val) {
		return (typeof val !== 'undefined' && val !== null && val !== '');
	},

	isMatch: function (val, expression, allowUndefined?) {
		if (!!allowUndefined && _isUndefined(val)) {
			return true;
		}
		return expression.test(val);
	},

	isEmail: function (val, allowUndefined?) {
		return validator.isMatch(val, EXPRESSIONS.email, allowUndefined);
	},

	isPhone: function (val, allowUndefined?) {
		if (!!allowUndefined && _isUndefined(val)) {
			return true;
		}
		try {
			return util.phoneHelper.testPossible(val) === 'IS_POSSIBLE';
		}
		catch (err) {
			return false;
		}
	},

	isEnum: function (val, enumKey, disallowZero?) {
		return (enums[enumKey].isValid(val) && (!disallowZero || val !== 0));
	},

	isDate: function (val, allowUndefined) {
		const isUndefined = _isUndefined(val);

		if (!!allowUndefined && isUndefined) {
			return true;
		}
		return !isUndefined && (val instanceof Date || (typeof val === 'string' && !isNaN(Date.parse(val))));
	},

	isString: function (val, allowUndefined?) {
		const isUndefined = _isUndefined(val);

		if (!!allowUndefined && isUndefined) {
			return true;
		}
		return !isUndefined && typeof val === 'string';
	},

	isNumber: function (val, allowUndefined?) {
		if (!!allowUndefined && _isUndefined(val)) {
			return true;
		}
		return !isNaN(parseFloat(val)) && isFinite(val);
	},

	isInteger: function (val, allowUndefined?) {
		if (!!allowUndefined && _isUndefined(val)) {
			return true;
		}
		return Number.isInteger(val);
	},

	isBool: function (val, allowUndefined?) {
		const isUndefined = _isUndefined(val);

		if (!!allowUndefined && isUndefined) {
			return true;
		}
		if (isUndefined) {
			return false;
		}

		val = val.toString().toLowerCase();
		return val === 'false' || val === 'true';
	}
};

export default validator;